import Accordion from "@/components/Accordion";
import { Select } from "@/components/Select";
import { PoliciesTable } from "@/components/Table";
import Shimmer from "@/components/Shimmer";
import { HeaderTextBlock } from "@/components/Typography";
import {
  useGetActiveLanguages,
  useGetLanguages,
  useGetPoliciesTable,
} from "@/requests/hooks/comply";
import {
  faCirclePlus,
  faFilePdf,
  faFileWord,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { IDropdownOption } from "@/components/Select/Select";

const Policies = () => {
  const { t } = useTranslation();
  const [activeLanguage, setActiveLanguage] = useState<
    IDropdownOption | undefined
  >();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: activeLanguages,
    isFetching: isFetchingGetActiveLanguages,
    isSuccess: isGetActiveLanguagesSuccess,
  } = useGetActiveLanguages();
  const {
    data: languagesData,
    isFetching: isFetchingGetLanguages,
    isSuccess: isGetLanguagesSuccess,
  } = useGetLanguages();
  const { isFetching: isFetchingGetPolicies } = useGetPoliciesTable({
    queryKey: ["comply", "list-policies"],
    enabled: true,
    params: {
      language: searchParams.get("language") || activeLanguage?.value || "",
      page: parseInt(searchParams.get("page") ?? "1"),
      pageSize: parseInt(searchParams.get("pageSize") ?? "25"),
    },
  });

  const getActiveLanguages = (): IDropdownOption[] | undefined => {
    const findActiveLanguage = (language: ILanguage) =>
      activeLanguages?.find(languageId => languageId === language?.id);

    return languagesData?.filter(findActiveLanguage).map(language => ({
      label: language.name,
      value: language.code,
    }));
  };

  useEffect(() => {
    if (
      isGetActiveLanguagesSuccess &&
      isGetLanguagesSuccess &&
      !activeLanguage
    ) {
      const firstItem = getActiveLanguages()?.[0];
      setActiveLanguage(firstItem);
      setSearchParams();
    }
  }, [isGetActiveLanguagesSuccess, isGetLanguagesSuccess]);

  useEffect(() => {
    if (
      !activeLanguage ||
      activeLanguage?.value === searchParams.get("language")
    )
      return;

    setSearchParams({ language: activeLanguage.value });
  }, [activeLanguage]);

  useEffect(() => {
    const searchParamLanguage = searchParams.get("language");

    if (
      !!searchParamLanguage &&
      searchParamLanguage !== activeLanguage?.value
    ) {
      setActiveLanguage({
        label: searchParamLanguage,
        value: searchParamLanguage,
      });
    }
  }, [searchParams.get("language")]);

  const onChangeActiveLanguage = (language: unknown) => {
    setActiveLanguage(language as IDropdownOption);
  };

  const renderHeaderTextBlock = () => (
    <div className="mb-30">
      <HeaderTextBlock
        featureText={t("comply.policies.header.feature").toUpperCase()}
        headingText={t("comply.policies.header.heading")}
        descriptionText={t("comply.policies.header.description")}
      />
    </div>
  );

  const renderLanguageSelectorSection = () => (
    <div className="mb-30 max-w-400">
      <Select
        label={t("comply.policies.languageSelector.label")}
        name="languagePolicy"
        options={getActiveLanguages()}
        isLoading={isFetchingGetActiveLanguages || isFetchingGetLanguages}
        value={activeLanguage}
        onChange={onChangeActiveLanguage}
        closeMenuOnSelect
      />
    </div>
  );

  const renderTable = () => (
    <PoliciesTable activeLanguage={activeLanguage?.value} />
  );

  const renderAccordionTopContent = () => (
    <div className="flex flex-grow items-center gap-20">
      <FontAwesomeIcon icon={faCirclePlus} className="h-20 w-20" />
      {t("comply.policies.add.title")}
    </div>
  );

  const renderAccordionItem = (item: "TEXT" | "PDF") => (
    <div className="border-gray-light *:[&:not(:last-child)]:border-b *:[&:not(:last-child)]:pb-10">
      <Link
        to={
          (item === "PDF" ? "add/pdf" : "add/text") +
          `?language=${activeLanguage?.value}`
        }
        className="flex flex-grow items-center justify-between hover:text-primary">
        <div className="flex items-center gap-20">
          <FontAwesomeIcon icon={faCirclePlus} className="h-20 w-20" />
          {item === "PDF"
            ? t("comply.policies.add.addPDFTitle")
            : t("comply.policies.add.addTextTitle")}
        </div>
        <FontAwesomeIcon
          icon={item === "PDF" ? faFilePdf : faFileWord}
          className="h-20 w-20"
        />
      </Link>
    </div>
  );

  const renderCreatePolicyAccordion = () => (
    <div className="mt-30">
      <Accordion topContent={renderAccordionTopContent()} defaultIsExpanded>
        <div className="flex flex-col gap-10">
          {renderAccordionItem("PDF")}
          {renderAccordionItem("TEXT")}
        </div>
      </Accordion>
    </div>
  );

  const renderPolicies = () => (
    <>
      {renderHeaderTextBlock()}
      {renderLanguageSelectorSection()}
      {renderTable()}
      {renderCreatePolicyAccordion()}
    </>
  );

  if (
    isFetchingGetPolicies ||
    isFetchingGetActiveLanguages ||
    isFetchingGetLanguages
  ) {
    return (
      <div className="flex flex-col gap-30">
        <Shimmer height={120} />
        <Shimmer height={400} />
        <Shimmer height={100} />
      </div>
    );
  }

  return renderPolicies();
};

export default Policies;
